import { ErrorResponse, OtpResponse, RegistrationResponse, ServiceResponse, ValidateOtpResponse } from '@bzbs/react-api-client';
import { create } from 'zustand';
import { useBuzzebeesAppStore } from './buzzebeess-app.store';
import { useLocaleStore } from './locale.store';
import { trackCommonEvents } from '../utils';

export type RegistrationState = {
  username: string;
  password: string;
  confirmPassword: string;
  contactNumber: string;
  firstName: string;
  lastName: string;
  otp: string;
  refCode: string;
  address?: string;
  gender?: string;
  birthDate?: number;
  email?: string;
  refUserCode?: string;
  consentVersion: {
    termAndCondition: string;
    dataPrivacy: string;
    marketingOptions: string;
  } | null;
  termAndConditionAccepted: boolean;
  dataPrivacyAccepted: boolean;
  marketingAccepted: boolean;
  emailMarketingAccepted: boolean;
  smsMarketingAccepted: boolean;
  notificationMarketingAccepted: boolean;
  lineMarketingAccepted: boolean;
  phoneMarketingAccepted: boolean;
  isLoading: boolean;
  error: ErrorResponse | null;
};

export type RegistrationActions = {
  setUsername: (value: string) => void;
  setPassword: (value: string) => void;
  setConfirmPassword: (value: string) => void;
  setContactNumber: (value: string) => void;
  setFirstName: (value: string) => void;
  setLastName: (value: string) => void;
  setOtp: (value: string) => void;
  setRefCode: (value: string) => void;
  setAddress: (value: string) => void;
  setGender: (value: string) => void;
  setBirthDate: (value: number) => void;
  setEmail: (value: string) => void;
  setRefUserCode: (value: string) => void;
  setTermAndConditionAccepted: (value: boolean) => void;
  setDataPrivacyAccepted: (value: boolean) => void;
  setMarketingAccepted: (value: boolean) => void;
  setEmailMarketingAccepted: (value: boolean) => void;
  setSmsMarketingAccepted: (value: boolean) => void;
  setNotificationMarketingAccepted: (value: boolean) => void;
  setLineMarketingAccepted: (value: boolean) => void;
  setPhoneMarketingAccepted: (value: boolean) => void;
  setError: (value: ErrorResponse | null) => void;
  setConsentVersion: (termAndCondition: string, dataPrivacy: string, marketingOptions: string) => void;
  clearData: () => void;
  sendOtp: () => Promise<ServiceResponse<OtpResponse>>;
  validateOtp: () => Promise<ServiceResponse<ValidateOtpResponse>>;
  register: () => Promise<ServiceResponse<RegistrationResponse>>;
  requestOTP: () => Promise<ServiceResponse<unknown>>;
  verifyOTP: () => Promise<ServiceResponse<unknown>>;
  clear: () => void;
};

export type RegistrationStore = RegistrationState & RegistrationActions;

export const useRegistrationStore = create<RegistrationStore>((set, get) => {

  return {
    username: '',
    password: '',
    confirmPassword: '',
    contactNumber: '',
    firstName: '',
    lastName: '',
    otp: '',
    refCode: '',
    address: undefined,
    gender: undefined,
    birthDate: undefined,
    email: undefined,
    refUserCode: undefined,
    consentVersion: null,
    termAndConditionAccepted: false,
    dataPrivacyAccepted: false,
    marketingAccepted: false,
    emailMarketingAccepted: false,
    smsMarketingAccepted: false,
    notificationMarketingAccepted: false,
    lineMarketingAccepted: false,
    phoneMarketingAccepted: false,
    isLoading: false,
    error: null,

    setUsername: (value: string) => set({ username: value }),
    setPassword: (value: string) => set({ password: value }),
    setConfirmPassword: (value: string) => set({ confirmPassword: value }),
    setContactNumber: (value: string) => set({ contactNumber: value }),
    setFirstName: (value: string) => set({ firstName: value }),
    setLastName: (value: string) => set({ lastName: value }),
    setOtp: (value: string) => set({ otp: value }),
    setRefCode: (value: string) => set({ refCode: value }),
    setAddress: (value: string) => set({ address: value }),
    setGender: (value: string) => set({ gender: value }),
    setBirthDate: (value: number) => set({ birthDate: value }),
    setEmail: (value: string) => set({ email: value }),
    setRefUserCode: (value: string) => set({ refUserCode: value }),
    setTermAndConditionAccepted: (value: boolean) => set({ termAndConditionAccepted: value }),
    setDataPrivacyAccepted: (value: boolean) => set({ dataPrivacyAccepted: value }),
    setMarketingAccepted: (value: boolean) => {
      set({ marketingAccepted: value });
      if (!value) {
        set({
          emailMarketingAccepted: false,
          smsMarketingAccepted: false,
          notificationMarketingAccepted: false,
          lineMarketingAccepted: false,
          phoneMarketingAccepted: false,
        });
      }
    },
    setEmailMarketingAccepted: (value: boolean) => set({ emailMarketingAccepted: value }),
    setSmsMarketingAccepted: (value: boolean) => set({ smsMarketingAccepted: value }),
    setNotificationMarketingAccepted: (value: boolean) => set({ notificationMarketingAccepted: value }),
    setLineMarketingAccepted: (value: boolean) => set({ lineMarketingAccepted: value }),
    setPhoneMarketingAccepted: (value: boolean) => set({ phoneMarketingAccepted: value }),
    setError: (value: ErrorResponse | null) => set({ error: value }),

    setConsentVersion: (termAndCondition: string, dataPrivacy: string, marketingOptions: string) => {
      set({
        consentVersion: {
          termAndCondition,
          dataPrivacy,
          marketingOptions,
        },
      });
    },

    clearData: () => {
      set({
        username: '',
        password: '',
        confirmPassword: '',
        contactNumber: '',
        firstName: '',
        lastName: '',
        otp: '',
        refCode: '',
        address: undefined,
        gender: undefined,
        birthDate: undefined,
        email: undefined,
        refUserCode: undefined,
        consentVersion: null,
        termAndConditionAccepted: false,
        dataPrivacyAccepted: false,
        marketingAccepted: false,
        emailMarketingAccepted: false,
        smsMarketingAccepted: false,
        notificationMarketingAccepted: false,
        lineMarketingAccepted: false,
        phoneMarketingAccepted: false,
        error: null,
      });
    },

    sendOtp: async () => {
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;
      const uuid = useBuzzebeesAppStore.getState().uuid;
      const appId = useBuzzebeesAppStore.getState().appId;
      const contactNumber = get().contactNumber;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      set({ isLoading: true });
      const response = await bzbsService.authApi.otp({
        uuid,
        appId,
        contactNumber: contactNumber.replace(/-/g, ''),
        channel: 'register',
      });
      set({ isLoading: false });

      if (response.type === 'success') {
        set({ refCode: response.model.refcode ?? '', error: null });
      } else {
        set({ error: response });
      }

      return response;
    },

    validateOtp: async () => {
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;
      const appId = useBuzzebeesAppStore.getState().appId;
      const otp = get().otp;
      const refCode = get().refCode;
      const contactNumber = get().contactNumber;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      set({ isLoading: true });
      const response = await bzbsService.authApi.validateOtp({
        appId,
        otp,
        refCode,
        contactNumber: contactNumber.replace(/-/g, ''),
        use: false,
        channel: 'register',
      });
      set({ isLoading: false });

      if (response.type === 'success') {
        set({ error: null });
      } else {
        set({ error: response });
      }

      return response;
    },

    register: async () => {
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;
      const uuid = useBuzzebeesAppStore.getState().uuid;
      const appId = useBuzzebeesAppStore.getState().appId;
      const macAddress = useBuzzebeesAppStore.getState().macAddress;
      const os = useBuzzebeesAppStore.getState().os;
      const platform = useBuzzebeesAppStore.getState().platform;
      const clientVersion = useBuzzebeesAppStore.getState().clientVersion;
      const deviceNotificationEnable = useBuzzebeesAppStore.getState().deviceNotificationEnabled;
      const contactNumber = get().contactNumber;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' }; 
      }

      set({ isLoading: true });
      const response = await bzbsService.registerApi.register({
        appId,
        uuid,
        macAddress,
        os,
        platform,
        clientVersion,
        deviceNotificationEnable,
        username: get().username,
        password: get().password,
        confirmPassword: get().confirmPassword,
        firstName: get().firstName,
        lastName: get().lastName,
        contactNumber: contactNumber.replace(/-/g, ''),
        otp: get().otp,
        refCode: get().refCode,
        termAndConditionVersion: get().consentVersion?.termAndCondition,
        dataPrivacyVersion: get().consentVersion?.dataPrivacy,
        marketingOptionsVersion: get().consentVersion?.marketingOptions,
        emailMarketing: get().emailMarketingAccepted ? '1' : '0',
        smsMarketing: get().smsMarketingAccepted ? '1' : '0',
        notificationMarketing: get().notificationMarketingAccepted ? '1' : '0',
        lineMarketing: get().lineMarketingAccepted ? '1' : '0',
        phoneMarketing: get().phoneMarketingAccepted ? '1' : '0',
        options: { device_locale: useLocaleStore.getState().localeId },
      }); 
      set({ isLoading: false });

      if (response.type === 'success') {  
        set({ error: null });
        trackCommonEvents.registerSuccess();
      } else {
        set({ error: response });
      }

      return response;
    },

    requestOTP: async () => {
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;
      const uuid = useBuzzebeesAppStore.getState().uuid;
      const appId = useBuzzebeesAppStore.getState().appId;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      set({ isLoading: true });
      const response = await bzbsService.authApi.otp({
        uuid,
        appId,
        contactNumber: get().contactNumber.replace(/-/g, ''),
        channel: 'register',
      }); 
      set({ isLoading: false });

      if (response.type === 'success') {
        set({ error: null });
      } else {
        set({ error: response });
      }

      return response;  
    },

    verifyOTP: async () => {
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;
      const appId = useBuzzebeesAppStore.getState().appId;
      const refCode = get().refCode;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      set({ isLoading: true });
      const response = await bzbsService.authApi.validateOtp({
        appId,
        otp: get().otp,
        refCode,
        contactNumber: get().contactNumber,
        use: false,
        channel: 'register',
      });
      set({ isLoading: false });

      if (response.type === 'success') {
        set({ error: null });
      } else {
        set({ error: response });
      }

      return response;
    },

    clear: () => {
      set({
        isLoading: false,
        error: null,
      });
    },
  };
});
